import { DestroyRef, Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PermissionResource } from '@coin/shared/core/util-enums';
import { EmployeeDto } from '@coin/shared/core/util-models';
import { take } from 'rxjs';
import { AuthService, EmulationService as SharedEmulationService } from '@coin/shared/auth/data-access';
import { SelectEmulatePersonComponent } from '@coin/shared/auth/feature';
import { PermissionsService } from '@coin/shared/auth/feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class EmulationService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly emulationService = inject(SharedEmulationService);
  private readonly dialog = inject(MatDialog);
  private readonly permissionsService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  public isEmulating = false;

  constructor() {
    this.initializeIsEmulating();
  }

  public isEmulationPermitted(): boolean {
    return this.permissionsService.hasPermissions(PermissionResource.Emulation);
  }

  public selectEmulationUser(): void {
    this.dialog
      .open(SelectEmulatePersonComponent, { maxWidth: '820px', width: '70vw', height: '90vh', minHeight: '400px' })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(emulateUser => {
        if (emulateUser) {
          this.emulateEmployee(emulateUser);
        }
      });
  }

  public emulateEmployee(employee: EmployeeDto): void {
    if (!employee) return;

    this.emulationService
      .emulateEmployee(employee.id, 'Emulation')
      .pipe(take(1))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          setTimeout(() => {
            this.router.navigate(['/']).then(() => {
              window.location.reload();
            });
          }, 100);
        },
        error: () => {
          this.toast.error(this.translate.instant('general.emulation-error'));
        }
      });
  }

  public removeEmulation(): void {
    this.authService.clearEmulation();
    setTimeout(() => {
      this.router.navigate(['/']).then(() => {
        window.location.reload();
      });
    }, 300);
  }

  private async initializeIsEmulating(): Promise<void> {
    this.isEmulating = !!(await this.authService.getEmulation());
  }
}
